/**
 * Audit Log Table Component
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { CheckCircle, XCircle, Eye, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const eventTypeColors = {
  'user.login': 'bg-green-500/20 text-green-400',
  'user.logout': 'bg-gray-500/20 text-gray-400',
  'user.register': 'bg-blue-500/20 text-blue-400',
  'content.create': 'bg-purple-500/20 text-purple-400',
  'content.delete': 'bg-red-500/20 text-red-400',
  'admin.action': 'bg-yellow-500/20 text-yellow-400',
  'moderation.flag': 'bg-orange-500/20 text-orange-400',
  'payment.create': 'bg-emerald-500/20 text-emerald-400'
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString();
};

export const AuditLogTable = ({ 
  logs, 
  loading, 
  page, 
  setPage, 
  total, 
  estimated,
  pageSize,
  onGetExactCount,
  loadingExactCount
}) => {
  const [selectedLog, setSelectedLog] = useState(null);
  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            Audit Logs
            {loading && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
          </CardTitle>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>
              {estimated ? '~' : ''}{total.toLocaleString()} total
            </span>
            {estimated && (
              <Button
                size="sm"
                variant="outline"
                onClick={onGetExactCount}
                disabled={loadingExactCount}
                className="h-7 text-xs border-gray-600"
              >
                {loadingExactCount ? 'Counting...' : 'Get exact count'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700 hover:bg-transparent">
                  <TableHead className="text-gray-400">Timestamp</TableHead>
                  <TableHead className="text-gray-400">Event</TableHead>
                  <TableHead className="text-gray-400">Entity</TableHead>
                  <TableHead className="text-gray-400">Actor</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400">IP</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="border-gray-700 hover:bg-gray-800/50">
                      <TableCell className="text-gray-300 text-sm">
                        {formatDate(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${eventTypeColors[log.event_type] || 'bg-gray-500/20 text-gray-400'} border-0`}>
                          {log.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-300">
                        <span className="text-xs text-gray-500">{log.entity_type}:</span>
                        <br />
                        <span className="font-mono text-xs">{log.entity_id?.slice(0, 8)}...</span>
                      </TableCell>
                      <TableCell className="text-gray-300 font-mono text-xs">
                        {log.actor_id?.slice(0, 8) || 'system'}...
                      </TableCell>
                      <TableCell>
                        {log.success ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                      </TableCell>
                      <TableCell className="text-gray-400 text-xs font-mono">
                        {log.ip_address || 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Page {page} of {totalPages || 1}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || loading}
                className="border-gray-600"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages || loading}
                className="border-gray-600"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Details Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Audit Log Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Event Type</p>
                  <Badge className={`${eventTypeColors[selectedLog.event_type] || 'bg-gray-500/20'} mt-1`}>
                    {selectedLog.event_type}
                  </Badge>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    {selectedLog.success ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-green-400">Success</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-400" />
                        <span className="text-red-400">Failed</span>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Timestamp</p>
                  <p className="text-white">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">IP Address</p>
                  <p className="text-white font-mono">{selectedLog.ip_address || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Entity</p>
                  <p className="text-white">{selectedLog.entity_type}: {selectedLog.entity_id}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Actor</p>
                  <p className="text-white font-mono">{selectedLog.actor_id || 'system'}</p>
                </div>
              </div>
              
              {selectedLog.details && (
                <div>
                  <p className="text-gray-400 text-sm mb-2">Details</p>
                  <pre className="bg-gray-800 p-4 rounded-lg text-sm text-gray-300 overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedLog.user_agent && (
                <div>
                  <p className="text-gray-400 text-sm">User Agent</p>
                  <p className="text-gray-300 text-sm">{selectedLog.user_agent}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuditLogTable;
